import { useEffect, useMemo, useState } from "react";
import { Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Paper, Stack, TextField } from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import * as XLSX from "xlsx";
import ConnString from "../../../../ConnString.js";

const apiRoot = ConnString.connstring;
const ALL = { Code: "__ALL__", Description: "All" };
const localToday = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const number = (value) => Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (value) => Number(value || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const escapeHtml = (value) => String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const formatAsOf = (value) => {
  const [year, month, day] = String(value || "").split("-");
  return year && month && day ? `${Number(month)}/${Number(day)}/${year}` : value;
};

const MultiSelect = ({ label, options, value, onChange, disabled }) => {
  const allSelected = options.length > 0 && value.length === options.length;
  return (
    <Autocomplete
      multiple disableCloseOnSelect disabled={disabled} options={[ALL, ...options]} value={value}
      onChange={(_, selected, reason, details) => {
        if (details?.option?.Code === ALL.Code) onChange(allSelected ? [] : options);
        else onChange(selected.filter((option) => option.Code !== ALL.Code));
      }}
      isOptionEqualToValue={(option, selected) => option.Code === selected.Code}
      getOptionLabel={(option) => option.Description || option.Code}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option.Code}>
          <Checkbox icon={<CheckBoxOutlineBlankIcon fontSize="small" />} checkedIcon={<CheckBoxIcon fontSize="small" />}
            checked={option.Code === ALL.Code ? allSelected : selected} sx={{ mr: 1 }} />
          {option.Code === ALL.Code ? "All" : `[${option.Code}] ${option.Description || ""}`}
        </li>
      )}
      renderTags={(selected, getTagProps) => allSelected
        ? <Chip label={`All (${options.length})`} size="small" />
        : selected.slice(0, 2).map((option, index) => <Chip {...getTagProps({ index })} key={option.Code} label={option.Code} size="small" />)}
      renderInput={(params) => <TextField {...params} label={label} placeholder={value.length ? "" : "Select one, multiple, or all"} />}
    />
  );
};

const RemainingInventoryReport = () => {
  const compID = sessionStorage.getItem("compID");
  const userID = sessionStorage.getItem("userID");
  const companyName = sessionStorage.getItem("compName") || sessionStorage.getItem("selectedCompany") || "Company";
  const companyTin = sessionStorage.getItem("compTIN") || "";
  const [dateAsOf, setDateAsOf] = useState(() => sessionStorage.getItem("remainingInventory.dateAsOf") || localToday());
  const [branches, setBranches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [sourceRows, setSourceRows] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState("");
  const [generated, setGenerated] = useState(false);

  const post = async (path, body, signal) => {
    const response = await fetch(`${apiRoot}${path}`, {
      method: "POST", credentials: "include", signal,
      headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
    const text = await response.text();
    let data;
    try { data = JSON.parse(text); } catch { throw new Error("The server returned an invalid response. Verify that the API server is running."); }
    if (!response.ok || data.success === false) throw new Error(data.message || "Request failed.");
    return data;
  };

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        setLoadingOptions(true); setError("");
        const data = await post("/api/inventory/reports/remaining/options", { compID, userID, branchCodes: [] }, controller.signal);
        const branchOptions = data.branches || [];
        setBranches(branchOptions); setCategories(data.categories || []);
        let saved = [];
        try { saved = JSON.parse(sessionStorage.getItem("remainingInventory.branches") || "[]"); } catch { saved = []; }
        setSelectedBranches(branchOptions.filter((option) => !saved.length || saved.includes(option.Code)));
      } catch (e) { if (e.name !== "AbortError") setError(e.message); }
      finally { if (!controller.signal.aborted) setLoadingOptions(false); }
    };
    if (compID && userID) load(); else { setError("Company or user information is missing. Please sign in again."); setLoadingOptions(false); }
    return () => controller.abort();
  }, [compID, userID]);

  useEffect(() => {
    if (!selectedBranches.length) { setWarehouses([]); setSelectedWarehouses([]); return undefined; }
    const controller = new AbortController();
    const load = async () => {
      try {
        const data = await post("/api/inventory/reports/remaining/options", {
          compID, userID, branchCodes: selectedBranches.map((option) => option.Code),
        }, controller.signal);
        const options = data.warehouses || [];
        setWarehouses(options);
        setSelectedWarehouses((current) => {
          const retained = options.filter((option) => current.some((item) => item.Code === option.Code));
          return current.length ? retained : options;
        });
      } catch (e) { if (e.name !== "AbortError") setError(e.message); }
    };
    load();
    return () => controller.abort();
  }, [compID, userID, selectedBranches]);

  const generate = async () => {
    if (!dateAsOf) return setError("Date as of is required.");
    if (!selectedBranches.length) return setError("Select at least one branch.");
    try {
      setLoadingReport(true); setError("");
      const data = await post("/api/inventory/reports/remaining", {
        compID, userID, dateAsOf,
        branchCodes: selectedBranches.map((option) => option.Code),
        warehouseCodes: selectedWarehouses.map((option) => option.Code),
        categoryCodes: selectedCategories.map((option) => option.Code),
      });
      setSourceRows(data.rows || []); setGenerated(true);
      sessionStorage.setItem("remainingInventory.dateAsOf", dateAsOf);
      sessionStorage.setItem("remainingInventory.branches", JSON.stringify(selectedBranches.map((option) => option.Code)));
    } catch (e) { setError(e.message); setSourceRows([]); setGenerated(true); }
    finally { setLoadingReport(false); }
  };

  const warehouseColumns = useMemo(() => {
    const map = new Map();
    sourceRows.forEach((row) => map.set(String(row.WarehouseCode), row.WarehouseName));
    return [...map].sort(([a], [b]) => a.localeCompare(b));
  }, [sourceRows]);

  const rows = useMemo(() => {
    const grouped = new Map();
    sourceRows.forEach((row) => {
      const key = `${row.CategoryCode}|${row.ItemCode}|${row.UnitBase}`;
      if (!grouped.has(key)) grouped.set(key, {
        id: key, CategoryCode: row.CategoryCode, CatDesciption: row.CatDesciption,
        ItemCode: row.ItemCode, ItemDescription: row.ItemDescription, UnitBase: row.UnitBase, TotalQty: 0, Amount: 0,
      });
      const item = grouped.get(key); const qty = Number(row.QTY || 0); const amount = Number(row.Amount || 0);
      item[`wh_${row.WarehouseCode}`] = Number(item[`wh_${row.WarehouseCode}`] || 0) + qty;
      item.TotalQty += qty; item.Amount += amount;
    });
    return [...grouped.values()].map((row) => ({ ...row, UnitCost: row.TotalQty ? row.Amount / row.TotalQty : 0 }));
  }, [sourceRows]);

  const totals = useMemo(() => rows.reduce((sum, row) => ({ qty: sum.qty + row.TotalQty, amount: sum.amount + row.Amount }), { qty: 0, amount: 0 }), [rows]);

  const groupedRows = useMemo(() => {
    const groups = new Map();
    rows.forEach((row) => {
      const key = row.CatDesciption || row.CategoryCode || "UNCATEGORIZED";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });
    return [...groups].sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);

  const groupTotals = (items) => items.reduce((sum, row) => {
    warehouseColumns.forEach(([code]) => { sum[`wh_${code}`] = Number(sum[`wh_${code}`] || 0) + Number(row[`wh_${code}`] || 0); });
    sum.TotalQty += Number(row.TotalQty || 0); sum.Amount += Number(row.Amount || 0);
    return sum;
  }, { TotalQty: 0, Amount: 0 });

  const printReport = () => {
    const popup = window.open("", "_blank");
    if (!popup) return setError("Allow pop-ups in the browser to print the report.");
    const headings = warehouseColumns.map(([code]) => `<th>${escapeHtml(code)}</th>`).join("");
    const body = groupedRows.map(([category, items]) => {
      const subtotal = groupTotals(items);
      const categoryRow = `<tr class="category"><td>${escapeHtml(category)}</td><td></td>${warehouseColumns.map(([code]) => `<td>${number(subtotal[`wh_${code}`])}</td>`).join("")}<td>${number(subtotal.TotalQty)}</td><td>${money(subtotal.TotalQty ? subtotal.Amount / subtotal.TotalQty : 0)}</td><td>${money(subtotal.Amount)}</td></tr>`;
      const details = items.map((row) => `<tr><td class="item">${escapeHtml(row.ItemCode)} - ${escapeHtml(row.ItemDescription)}</td><td class="unit">${escapeHtml(row.UnitBase)}</td>${warehouseColumns.map(([code]) => `<td>${Number(row[`wh_${code}`] || 0) ? number(row[`wh_${code}`]) : ""}</td>`).join("")}<td>${number(row.TotalQty)}</td><td>${money(row.UnitCost)}</td><td>${money(row.Amount)}</td></tr>`).join("");
      return categoryRow + details;
    }).join("");
    const footer = `<tr class="grand"><td>TOTAL</td><td></td>${warehouseColumns.map(([code]) => `<td>${number(rows.reduce((sum, row) => sum + Number(row[`wh_${code}`] || 0), 0))}</td>`).join("")}<td>${number(totals.qty)}</td><td>${money(totals.qty ? totals.amount / totals.qty : 0)}</td><td>${money(totals.amount)}</td></tr>`;
    popup.document.write(`<!doctype html><html><head><title>Inventory remaining report</title><style>@page{size:A4 landscape;margin:8mm}*{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#111;margin:0;font-size:9px}.head{font-weight:700;font-size:10px;line-height:1.35}.title{font-size:11px}.period{margin:2px 0 4px}table{width:100%;border-collapse:collapse;table-layout:fixed}th,td{border:1px solid #111;padding:2px 3px}th{font-size:9px;text-align:center}th:first-child{width:43%;text-align:left}th:nth-child(2){width:6%}th:last-child,th:nth-last-child(2),th:nth-last-child(3){width:9%}td{text-align:right;white-space:nowrap}.item{text-align:left;padding-left:28px;overflow:hidden;text-overflow:ellipsis}.unit{text-align:center}.category td,.grand td{font-weight:700;font-size:10px}.category td:first-child,.grand td:first-child{text-align:left}.footer{position:fixed;bottom:0;font-size:8px}</style></head><body><div class="head">${escapeHtml(companyName)}<br>${escapeHtml(companyTin)}<br><span class="title">STOCK ON HAND</span><br><span class="period">As of: ${escapeHtml(formatAsOf(dateAsOf))}</span></div><table><thead><tr><th>Item Description</th><th>Unit</th>${headings}<th>Total</th><th>Unit Cost</th><th>Amount</th></tr></thead><tbody>${body}${footer}</tbody></table><div class="footer">${escapeHtml(new Date().toLocaleString("en-PH"))}</div><script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();};</script></body></html>`);
    popup.document.close();
  };

  const exportToExcel = () => {
    const warehouseCodes = warehouseColumns.map(([code]) => code);
    const sheetRows = [
      [companyName],
      [companyTin],
      ["STOCK ON HAND"],
      [`As of: ${formatAsOf(dateAsOf)}`],
      [],
      ["Item Description", "Unit", ...warehouseCodes, "Total", "Unit Cost", "Amount"],
    ];

    groupedRows.forEach(([category, items]) => {
      const subtotal = groupTotals(items);
      sheetRows.push([
        category,
        "",
        ...warehouseCodes.map((code) => Number(subtotal[`wh_${code}`] || 0)),
        subtotal.TotalQty,
        subtotal.TotalQty ? subtotal.Amount / subtotal.TotalQty : 0,
        subtotal.Amount,
      ]);
      items.forEach((row) => sheetRows.push([
        `${row.ItemCode} - ${row.ItemDescription}`,
        row.UnitBase,
        ...warehouseCodes.map((code) => Number(row[`wh_${code}`] || 0)),
        row.TotalQty,
        row.UnitCost,
        row.Amount,
      ]));
    });

    sheetRows.push([
      "TOTAL",
      "",
      ...warehouseCodes.map((code) => rows.reduce((sum, row) => sum + Number(row[`wh_${code}`] || 0), 0)),
      totals.qty,
      totals.qty ? totals.amount / totals.qty : 0,
      totals.amount,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);
    worksheet["!cols"] = [
      { wch: 65 }, { wch: 12 },
      ...warehouseCodes.map(() => ({ wch: 13 })),
      { wch: 14 }, { wch: 16 }, { wch: 18 },
    ];
    worksheet["!freeze"] = { xSplit: 2, ySplit: 6 };

    const headerRow = 5;
    const lastColumn = sheetRows[headerRow].length - 1;
    for (let column = 2; column <= lastColumn; column += 1) {
      for (let row = headerRow + 1; row < sheetRows.length; row += 1) {
        const address = XLSX.utils.encode_cell({ r: row, c: column });
        if (worksheet[address]) worksheet[address].z = "#,##0.00;[Red]-#,##0.00";
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock on Hand");
    XLSX.writeFile(workbook, `Inventory remaining report ${dateAsOf}.xlsx`);
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(2, minmax(220px, 1fr))", xl: "repeat(4, minmax(220px, 1fr))" }} gap={2}>
          <TextField label="Date as of" type="date" value={dateAsOf} onChange={(event) => setDateAsOf(event.target.value)} InputLabelProps={{ shrink: true }} required />
          <MultiSelect label="Branch" options={branches} value={selectedBranches} onChange={setSelectedBranches} disabled={loadingOptions} />
          <MultiSelect label="Warehouse" options={warehouses} value={selectedWarehouses} onChange={setSelectedWarehouses} disabled={!selectedBranches.length} />
          <MultiSelect label="Item Category" options={categories} value={selectedCategories} onChange={setSelectedCategories} disabled={loadingOptions} />
        </Box>
        <Stack direction="row" justifyContent="flex-end" mt={2}>
          <Button variant="contained" startIcon={loadingReport ? <CircularProgress color="inherit" size={17} /> : <PlayArrowRoundedIcon />}
            disabled={loadingReport || loadingOptions} onClick={generate} sx={{ minWidth: 150 }}>Generate Report</Button>
        </Stack>
      </Paper>
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button variant="outlined" startIcon={<FileDownloadRoundedIcon />} onClick={exportToExcel} disabled={!generated || !rows.length}>Export Excel</Button>
        <Button variant="outlined" startIcon={<PrintRoundedIcon />} onClick={printReport} disabled={!generated || !rows.length}>Print Report</Button>
      </Stack>
      <Paper variant="outlined" sx={{ borderRadius: 0, overflowX: "auto", bgcolor: "#fff", color: "#111", p: 1.5 }}>
        <Box minWidth={Math.max(1050, 650 + warehouseColumns.length * 105)}>
          <Box fontFamily="Arial, sans-serif" fontWeight={700} fontSize={13} lineHeight={1.3} mb={0.5}>
            <div>{companyName}</div>
            {companyTin && <div>{companyTin}</div>}
            <div style={{ fontSize: 14 }}>STOCK ON HAND</div>
            <div>As of: {formatAsOf(dateAsOf)}</div>
          </Box>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", fontFamily: "Arial, sans-serif", fontSize: 12,
            "th, td": { border: "1px solid #111", px: 0.5, py: 0.25 },
            "th": { fontWeight: 800, textAlign: "center" },
            "th:first-of-type": { width: "43%", textAlign: "left" },
            "th:nth-of-type(2)": { width: 75 },
            "td": { textAlign: "right", whiteSpace: "nowrap" },
          }}>
            <thead><tr><th>Item Description</th><th>Unit</th>{warehouseColumns.map(([code, description]) => <th key={code} title={description}>{code}</th>)}<th>Total</th><th>Unit Cost</th><th>Amount</th></tr></thead>
            <tbody>
              {!generated && <tr><td colSpan={warehouseColumns.length + 5} style={{ textAlign: "center", padding: 28, color: "#666" }}>Set the parameters and generate the report.</td></tr>}
              {generated && !rows.length && <tr><td colSpan={warehouseColumns.length + 5} style={{ textAlign: "center", padding: 28, color: "#666" }}>No inventory matched the selected parameters.</td></tr>}
              {groupedRows.map(([category, items]) => {
                const subtotal = groupTotals(items);
                return [
                  <tr key={`category-${category}`} style={{ fontWeight: 800, fontSize: 13 }}>
                    <td style={{ textAlign: "left" }}>{category}</td><td />
                    {warehouseColumns.map(([code]) => <td key={code}>{number(subtotal[`wh_${code}`])}</td>)}
                    <td>{number(subtotal.TotalQty)}</td><td>{money(subtotal.TotalQty ? subtotal.Amount / subtotal.TotalQty : 0)}</td><td>{money(subtotal.Amount)}</td>
                  </tr>,
                  ...items.map((row) => <tr key={row.id}>
                    <td style={{ textAlign: "left", paddingLeft: 28 }}>{row.ItemCode} - {row.ItemDescription}</td><td style={{ textAlign: "center" }}>{row.UnitBase}</td>
                    {warehouseColumns.map(([code]) => <td key={code}>{Number(row[`wh_${code}`] || 0) ? number(row[`wh_${code}`]) : ""}</td>)}
                    <td>{number(row.TotalQty)}</td><td>{money(row.UnitCost)}</td><td>{money(row.Amount)}</td>
                  </tr>),
                ];
              })}
              {!!rows.length && <tr style={{ fontWeight: 800, fontSize: 13 }}>
                <td style={{ textAlign: "left" }}>TOTAL</td><td />
                {warehouseColumns.map(([code]) => <td key={code}>{number(rows.reduce((sum, row) => sum + Number(row[`wh_${code}`] || 0), 0))}</td>)}
                <td>{number(totals.qty)}</td><td>{money(totals.qty ? totals.amount / totals.qty : 0)}</td><td>{money(totals.amount)}</td>
              </tr>}
            </tbody>
          </Box>
        </Box>
      </Paper>
    </Stack>
  );
};

export default RemainingInventoryReport;
